import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard,
  FileText,
  Users,
  CheckCircle2,
  BarChart3,
  PlusCircle,
  LogOut,
  CalendarDays,
  ScrollText,
  Settings as SettingsIcon,
  KeyRound,
} from "lucide-react";

const adminLinks = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-admin-overview" },
  { to: "/admin/approvals", label: "Approvals", icon: CheckCircle2, testid: "nav-admin-approvals" },
  { to: "/admin/invoices", label: "All Invoices", icon: FileText, testid: "nav-admin-invoices" },
  { to: "/admin/leaves", label: "Leaves", icon: CalendarDays, testid: "nav-admin-leaves" },
  { to: "/admin/salespeople", label: "Users", icon: Users, testid: "nav-admin-salespeople" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, testid: "nav-admin-reports" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testid: "nav-admin-audit" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-admin-settings" },
  { to: "/admin/password", label: "Change Password", icon: KeyRound, testid: "nav-admin-password" },
];

const adminPersonalLinks = [
  { to: "/dashboard", label: "My Dashboard", icon: LayoutDashboard, end: true, testid: "nav-admin-personal-dashboard" },
  { to: "/invoices/new", label: "New Invoice", icon: PlusCircle, testid: "nav-admin-personal-new" },
  { to: "/leaves", label: "My Leaves", icon: CalendarDays, testid: "nav-admin-personal-leaves" },
];

const salesLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-sales-dashboard" },
  { to: "/invoices", label: "My Invoices", icon: FileText, testid: "nav-sales-invoices" },
  { to: "/invoices/new", label: "New Invoice", icon: PlusCircle, testid: "nav-sales-new" },
  { to: "/leaves", label: "My Leaves", icon: CalendarDays, testid: "nav-sales-leaves" },
  { to: "/password", label: "Change Password", icon: KeyRound, testid: "nav-sales-password" },
];

const dataEntryLinks = [
  { to: "/invoices", label: "Invoices", icon: FileText, end: true, testid: "nav-de-invoices" },
  { to: "/invoices/new", label: "New Invoice", icon: PlusCircle, testid: "nav-de-new" },
  { to: "/leaves", label: "My Leaves", icon: CalendarDays, testid: "nav-de-leaves" },
  { to: "/password", label: "Change Password", icon: KeyRound, testid: "nav-de-password" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isDataEntry = user?.role === "data_entry";
  const links = isAdmin ? adminLinks : isDataEntry ? dataEntryLinks : salesLinks;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="w-64 shrink-0 border-r border-zinc-200 bg-white h-screen sticky top-0 flex flex-col">
      <div className="p-5 border-b border-blue-100 shrink-0 bg-gradient-to-b from-blue-50 to-white">
        <div className="bg-black rounded-md p-2 flex items-center justify-center">
          <Logo size={36} />
        </div>
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-700 text-center">
          Sales Tracker
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-800 border-r-2 border-blue-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon size={18} strokeWidth={1.5} />
              {l.label}
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            <div className="mt-5 mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Personal</div>
            {adminPersonalLinks.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={l.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-800 border-r-2 border-blue-700"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  <Icon size={18} strokeWidth={1.5} />
                  {l.label}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-blue-100 shrink-0 bg-blue-50/50">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold text-sm">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-950 truncate" data-testid="sidebar-user-name">
              {user?.name}
            </div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950 transition-colors duration-200"
        >
          <LogOut size={16} strokeWidth={1.5} /> Logout
        </button>
      </div>
    </aside>
  );
}
